; (() => {
	function initAccordionInstance(root) {
		if (!root || root.dataset.accordionInitialized === 'true') return
		root.dataset.accordionInitialized = 'true'

		const triggers = Array.from(root.querySelectorAll('.accordion-trigger'))
		if (!triggers.length) return
		
		triggers.forEach((trigger) => {
			const panelId = trigger.getAttribute('aria-controls')
			if (!panelId) return

			// Robust selector (even if IDs contain weird chars)
			const content = root.querySelector(`#${CSS.escape(panelId)}`)
			if (!content) return

			setupItem(root, triggers, trigger, content)
		})
	}

	function setupItem(root, triggers, trigger, content) {
		const wasHidden = content.classList.contains('content-hidden')
		if (wasHidden) content.classList.remove('content-hidden')
		const measuredHeight = content.scrollHeight
		if (wasHidden) content.classList.add('content-hidden')

		const isInitiallyOpen =
			trigger.getAttribute('aria-expanded') === 'true' ||
			content.getAttribute('data-state') === 'open'

		content.style.setProperty(
			'--wus-accordion-content-height',
			isInitiallyOpen ? `${measuredHeight}px` : '0px'
		)

		trigger.setAttribute('data-state', isInitiallyOpen ? 'open' : 'closed')
		trigger.setAttribute('aria-expanded', String(isInitiallyOpen))

		content.setAttribute('data-state', isInitiallyOpen ? 'open' : 'closed')
		content.setAttribute('aria-hidden', String(!isInitiallyOpen))

		if (isInitiallyOpen) content.classList.remove('content-hidden')
		else content.classList.add('content-hidden')

		content.addEventListener('animationend', () => {
			const state = content.getAttribute('data-state')
			if (state === 'closed') {
				content.classList.add('content-hidden')
				content.style.setProperty('--wus-accordion-content-height', '0px')
				content.setAttribute('aria-hidden', 'true')
			} else {
				content.classList.remove('content-hidden')
				content.setAttribute('aria-hidden', 'false')
			}
		})

		trigger.addEventListener('click', () => {
			function initCardSliderInstance(root) {
				if (!root || root.dataset.cardSliderInitialized === 'true') return
				root.dataset.cardSliderInitialized = 'true'

				const track = root.querySelector('.wus-ingredients__track')
				if (!track) return

				const cards = Array.from(root.querySelectorAll('.wus-ingredient[data-card-slider-card]'))
				if (!cards.length) return

				cards.forEach((card) => setupCard(root, track, cards, card))

				const btnPrev = root.querySelector('.slider__control.control_prev')
				const btnNext = root.querySelector('.slider__control.control_next')
				setupControls(root, track, cards, btnPrev, btnNext)

				const onResize = debounce(() => {
					const isMobile = window.matchMedia('(max-width: 749px)').matches
					if (!isMobile) return

					const openCard = root.querySelector('.wus-ingredient.is-open[data-card-slider-card]')
					if (!openCard) return

					const panel = openCard.querySelector('[data-card-slider-panel]')
					if (!panel) return
					updateMobilePanelHeight(panel)
				}, 150)

				window.addEventListener('resize', onResize)
				root._cardSliderOnResize = onResize
			}

			function setupCard(root, track, cards, card) {
				const toggles = Array.from(card.querySelectorAll('[data-card-slider-toggle]'))
				const panel = card.querySelector('[data-card-slider-panel]')
				if (!toggles.length || !panel) return

				const isInitiallyOpen = panel.getAttribute('data-state') === 'open'
				setPanelState(card, panel, toggles, isInitiallyOpen)

				// Ensure correct initial height on mobile if a card starts open
				if (isInitiallyOpen && window.matchMedia('(max-width: 749px)').matches) {
					updateMobilePanelHeight(panel)
				}

				toggles.forEach((toggle) => {
					toggle.addEventListener('click', () => {
						const isOpen = panel.getAttribute('data-state') === 'open'

						if (isOpen) {
							closePanel(card, panel, toggles)
							return
						}

						// Opening: close all others, open this one, snap to it
						closeOtherPanels(root, card)
						openPanel(card, panel, toggles)

						// Mobile: set measured height for smooth "open down"
						if (window.matchMedia('(max-width: 749px)').matches) {
							updateMobilePanelHeight(panel)
						}

						snapCardIntoView(track, card)
					})
				})
			}

			function openPanel(card, panel, toggles) {
				setPanelState(card, panel, toggles, true)
			}

			function closePanel(card, panel, toggles) {
				setPanelState(card, panel, toggles, false)

				// Collapse height on smaller screens
				panel.style.setProperty('--wus-card-slider-panel-height', '0px')
			}

			function closeOtherPanels(root, exceptCard) {
				const openCards = Array.from(
					root.querySelectorAll('.wus-ingredient.is-open[data-card-slider-card]')
				)

				openCards.forEach((card) => {
					if (card === exceptCard) return

					const panel = card.querySelector('[data-card-slider-panel]')
					const toggles = Array.from(card.querySelectorAll('[data-card-slider-toggle]'))
					if (!panel || !toggles.length) return

					setPanelState(card, panel, toggles, false)
					panel.style.setProperty('--wus-card-slider-panel-height', '0px')
				})
			}

			function closeAllPanels(root) {
				// Close everything (used when navigating prev/next)
				closeOtherPanels(root, null)
			}

			function setPanelState(card, panel, toggles, open) {
				panel.setAttribute('data-state', open ? 'open' : 'closed')

				toggles.forEach((toggle) => {
					toggle.setAttribute('aria-expanded', String(open))
				})

				card.classList.toggle('is-open', open)
			}

			function updateMobilePanelHeight(panel) {
				const inner = panel.querySelector('.wus-ingredient__panel-inner')
				if (!inner) return

				const styles = window.getComputedStyle(inner)
				const paddingTop = parseFloat(styles.paddingTop) || 0
				const paddingBottom = parseFloat(styles.paddingBottom) || 0
				const target = inner.scrollHeight + paddingTop + paddingBottom

				panel.style.setProperty('--wus-card-slider-panel-height', `${target}px`)
			}

			function snapCardIntoView(track, card) {
				requestAnimationFrame(() => {
					card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
				})
			}

			function setupControls(root, track, cards, btnPrev, btnNext) {
				let activeIndex = getClosestCardIndex(track, cards)

				function updateDisabledStates() {
					if (btnPrev) btnPrev.disabled = activeIndex <= 0
					if (btnNext) btnNext.disabled = activeIndex >= cards.length - 1
				}

				function scrollToIndex(index) {
					closeAllPanels(root)

					activeIndex = clamp(index, 0, cards.length - 1)
					const el = cards[activeIndex]
					if (!el) return
					el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
					updateDisabledStates()
				}

				if (btnPrev) btnPrev.addEventListener('click', () => scrollToIndex(activeIndex - 1))
				if (btnNext) btnNext.addEventListener('click', () => scrollToIndex(activeIndex + 1))

				const onScroll = debounce(() => {
					activeIndex = getClosestCardIndex(track, cards)
					updateDisabledStates()
				}, 80)

				track.addEventListener('scroll', onScroll, { passive: true })
				updateDisabledStates()
			}

			function getClosestCardIndex(track, cards) {
				const trackLeft = track.getBoundingClientRect().left
				let bestIndex = 0
				let bestDist = Infinity

				cards.forEach((card, idx) => {
					const left = card.getBoundingClientRect().left
					const dist = Math.abs(left - trackLeft)
					if (dist < bestDist) {
						bestDist = dist
						bestIndex = idx
					}
				})

				return bestIndex
			}

			function clamp(n, min, max) {
				return Math.max(min, Math.min(max, n))
			}

			function debounce(fn, wait) {
				let t
				return (...args) => {
					window.clearTimeout(t)
					t = window.setTimeout(() => fn(...args), wait)
				}
			}

			function init(rootEl) {
				if (rootEl) {
					initCardSliderInstance(rootEl)
					return
				}

				const roots = document.querySelectorAll('[data-module="card-slider"]')
				if (!roots.length) return
				roots.forEach((root) => initCardSliderInstance(root))
			}

			document.addEventListener('DOMContentLoaded', () => init())

			document.addEventListener('shopify:section:load', (event) => {
				const section = event.target
				if (!section) return
				section.querySelectorAll('[data-module="card-slider"]').forEach((root) => init(root))
			})

			document.addEventListener('shopify:section:unload', (event) => {
				const section = event.target
				if (!section) return
				section.querySelectorAll('[data-module="card-slider"]').forEach((root) => {
					delete root.dataset.cardSliderInitialized

					if (root._cardSliderOnResize) {
						window.removeEventListener('resize', root._cardSliderOnResize)
						delete root._cardSliderOnResize
					}
				})
			})
			const isOpen = trigger.getAttribute('data-state') === 'open'
			isOpen ? closeItem(trigger, content) : openItem(root, trigger, content)
		})

		trigger.addEventListener('keydown', (event) => {
			const key = event.key
			const index = triggers.indexOf(trigger)
			if (index === -1) return

			if (key === 'ArrowDown') {
				event.preventDefault()
				triggers[(index + 1) % triggers.length].focus()
			}
			if (key === 'ArrowUp') {
				event.preventDefault()
				triggers[(index - 1 + triggers.length) % triggers.length].focus()
			}
			if (key === 'Home') {
				event.preventDefault()
				triggers[0].focus()
			}
			if (key === 'End') {
				event.preventDefault()
				triggers[triggers.length - 1].focus()
			}
		})
	}

	function openItem(root, trigger, content) {
		const others = root.querySelectorAll('.accordion-trigger[data-state="open"]')
		others.forEach((otherTrigger) => {
			if (otherTrigger !== trigger) {
				const otherId = otherTrigger.getAttribute('aria-controls')
				if (!otherId) return
				const otherContent = root.querySelector(`#${CSS.escape(otherId)}`)
				if (otherContent) closeItem(otherTrigger, otherContent)
			}
		})

		content.classList.remove('content-hidden')
		const height = content.scrollHeight
		content.style.setProperty('--wus-accordion-content-height', `${height}px`)

		trigger.setAttribute('data-state', 'open')
		trigger.setAttribute('aria-expanded', 'true')

		content.setAttribute('data-state', 'open')
		content.setAttribute('aria-hidden', 'false')
	}

	function closeItem(trigger, content) {
		content.classList.remove('content-hidden')
		const height = content.scrollHeight
		content.style.setProperty('--wus-accordion-content-height', `${height}px`)

		trigger.setAttribute('data-state', 'closed')
		trigger.setAttribute('aria-expanded', 'false')

		content.setAttribute('data-state', 'closed')
		content.setAttribute('aria-hidden', 'true')
	}

	function initAccordions(rootEl) {
		const roots = rootEl
			? rootEl.querySelectorAll('[data-module="accordion"]')
			: document.querySelectorAll('[data-module="accordion"]')

		if (!roots.length) return
		roots.forEach((root) => initAccordionInstance(root))
	}

	const boot = () => initAccordions()
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot)
	} else {
		boot()
	}

	// Theme editor
	document.addEventListener('shopify:section:load', (event) => {
		initAccordions(event.target)
	})

	document.addEventListener('shopify:section:unload', (event) => {
		event.target?.querySelectorAll?.('[data-module="accordion"]').forEach((root) => {
			delete root.dataset.accordionInitialized
		})
	})
})()